import { useState } from 'react';
import { toast } from 'sonner';
import { useSpaceWrite } from '@/hooks/queries/useSpaceWrite';
import { spacesApi } from '@/lib/api';
import { settingsError } from '@/lib/spaces/settings';
import type { SetSpaceBudgetRequest, SpaceBudget } from '@/types/spaces';

// Saving the budget replaces it as a whole, so the next edit must start from what the API holds — never from a copy
// that predates this save. The PUT answers with the budget read for the CURRENT period (it takes no period), so it
// only stands in for this page's period when it IS that period; otherwise the page's period is read explicitly before
// the editor frees up. SWR's own revalidation would not do: it swallows a failed GET and keeps the old copy. If that
// read fails, this period's cache is cleared, so the page shows its failure and retry instead of a budget older than
// the save. Every other cached period of the space is cleared too (its limit lines predate the save), and a failed
// save clears them all. The total limit lives on the space itself, so the space detail and the audit trail are
// refreshed as well.
export function useSpaceBudgetEditor(spaceId: string, period: string) {
  const { write, mutate, refresh, userId } = useSpaceWrite();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageKey = ['space-budget', userId, spaceId, period];
  const budgetOf = (key: unknown): boolean => Array.isArray(key) && key[0] === 'space-budget' && key[1] === userId && key[2] === spaceId;

  const settlePageBudget = async (saved: SpaceBudget): Promise<void> => {
    try {
      const fresh = saved.periodKey === period ? saved : await write((token) => spacesApi.budget(token, spaceId, period));
      await mutate(pageKey, fresh, { revalidate: false });
    } catch {
      void mutate(pageKey, undefined, { revalidate: true }).catch(() => undefined);
    }
  };

  const save = async (request: SetSpaceBudgetRequest): Promise<boolean> => {
    if (busy) return false;
    setBusy(true); setError(null);
    try {
      const saved = await write((token) => spacesApi.setBudget(token, spaceId, request));
      toast.success('Orçamento atualizado.');
      void mutate((key) => budgetOf(key) && Array.isArray(key) && key[3] !== period, undefined, { revalidate: true }).catch(() => undefined);
      await settlePageBudget(saved);
      return true;
    } catch (failure) {
      // The API owns the taxonomy rules (income, outside the budget): its message is the one worth showing.
      const message = settingsError(failure, 'Não foi possível salvar o orçamento. Tente novamente.');
      setError(message); toast.error(message);
      void mutate(budgetOf, undefined, { revalidate: true }).catch(() => undefined);
      return false;
    } finally {
      refresh(['space', userId, spaceId]);
      void mutate((key) => Array.isArray(key) && key[0] === 'space-audit' && key[1] === userId && key[2] === spaceId).catch(() => undefined);
      setBusy(false);
    }
  };
  const clearError = () => setError(null);

  return { busy, error, save, clearError };
}

// One per budget sheet: every save replaces the whole budget, so the sheet and its editor share one busy state — a
// second instance would let one surface open or save from a budget the other is replacing.
export type SpaceBudgetEditorState = ReturnType<typeof useSpaceBudgetEditor>;
